import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Badge } from "./components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./components/ui/dialog";
import { 
  Search, Calendar, ChevronLeft, ChevronRight, Check, X, AlertTriangle,
  User, Filter, Download, RefreshCw, Save, Plus
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const AnwesenheitNeu = () => {
  const [teilnehmer, setTeilnehmer] = useState([]);
  const [selectedTeilnehmer, setSelectedTeilnehmer] = useState(null);
  const [anwesenheit, setAnwesenheit] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [filter, setFilter] = useState(''); // Alle Teilnehmer als Standard
  
  // Dialog für Entschuldigung
  const [grundDialog, setGrundDialog] = useState({ open: false, item: null, grund: '' });

  // Teilnehmer laden
  const loadTeilnehmer = useCallback(async () => {
    try {
      setLoading(true);
      const params = filter ? `?finanzierungsart=${filter}` : '';
      const res = await axios.get(`${API}/teilnehmer${params}`);
      setTeilnehmer(res.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Teilnehmer');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadTeilnehmer();
  }, [loadTeilnehmer]);

  // Anwesenheit für ausgewählten Teilnehmer laden
  const loadAnwesenheit = useCallback(async () => {
    if (!selectedTeilnehmer) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/bg-anwesenheit/teilnehmer/${selectedTeilnehmer.id}`);
      setAnwesenheit(res.data);
      
      // Setze den Monat IMMER auf den aktuellen Monat (heute)
      setCurrentMonth(new Date());
    } catch (error) {
      console.error('Fehler beim Laden der Anwesenheit');
      setAnwesenheit([]);
    } finally {
      setLoading(false);
    }
  }, [selectedTeilnehmer]);

  useEffect(() => {
    loadAnwesenheit();
  }, [loadAnwesenheit]);

  // Gefilterte Teilnehmer
  const filteredTeilnehmer = teilnehmer.filter(tn => {
    const searchLower = search.toLowerCase();
    return (
      tn.vorname?.toLowerCase().includes(searchLower) ||
      tn.nachname?.toLowerCase().includes(searchLower) ||
      tn.kurs?.toLowerCase().includes(searchLower)
    );
  });

  // Status ändern
  const updateStatus = async (anwesenheitId, newStatus, grund = '') => {
    setSaving(true);
    try {
      await axios.put(`${API}/bg-anwesenheit/${anwesenheitId}`, {
        status: newStatus,
        grund: grund
      });
      toast.success('Status aktualisiert');
      loadAnwesenheit();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  // Schnell-Status-Änderung (Klick-Rotation: anwesend -> entschuldigt -> unentschuldigt -> anwesend)
  const cycleStatus = async (item) => {
    const statusOrder = ['anwesend', 'entschuldigt', 'unentschuldigt'];
    const currentIndex = statusOrder.indexOf(item.status);
    const newStatus = statusOrder[(currentIndex + 1) % 3];
    
    if (newStatus === 'entschuldigt') {
      // Dialog für Grund öffnen
      setGrundDialog({ open: true, item: item, grund: item.grund || 'AU' });
    } else {
      await updateStatus(item.id, newStatus);
    }
  };

  // Entschuldigung mit Grund speichern
  const saveEntschuldigung = async () => {
    if (grundDialog.item) {
      await updateStatus(grundDialog.item.id, 'entschuldigt', grundDialog.grund);
    }
    setGrundDialog({ open: false, item: null, grund: '' });
  };

  // Tage im aktuellen Monat
  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
      days.push(new Date(d));
    }
    return days;
  };

  // Anwesenheit für einen Tag finden
  const getAnwesenheitForDay = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return anwesenheit.filter(a => a.datum === dateStr);
  };

  // Statistik berechnen
  const getStatistik = () => {
    if (anwesenheit.length === 0) return { anwesend: 0, entschuldigt: 0, unentschuldigt: 0, quote: 0 };
    
    const anwesend = anwesenheit.filter(a => a.status === 'anwesend').length;
    const entschuldigt = anwesenheit.filter(a => a.status === 'entschuldigt').length;
    const unentschuldigt = anwesenheit.filter(a => a.status === 'unentschuldigt').length;
    const quote = Math.round((anwesend / anwesenheit.length) * 100);
    
    return { anwesend, entschuldigt, unentschuldigt, quote, total: anwesenheit.length };
  };

  const stats = getStatistik();

  // Monat Navigation
  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  };

  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  return (
    <div className="page-container">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2" style={{fontFamily: 'Manrope, sans-serif'}}>
            <Calendar className="text-[#D9775A]" size={28} />
            Anwesenheit bearbeiten
          </h1>
          <p className="text-sm text-gray-500">Wählen Sie einen Teilnehmer und klicken Sie auf einen Status zum Ändern</p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Linke Spalte: Teilnehmer-Liste */}
        <div className="col-span-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <User size={18} /> Teilnehmer auswählen
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Suche */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <Input
                  placeholder="Name oder Kurs suchen..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* Filter */}
              <div className="flex gap-2 mb-4 flex-wrap">
                <Button 
                  size="sm" 
                  variant={filter === '' ? 'default' : 'outline'}
                  onClick={() => setFilter('')}
                  className={filter === '' ? 'bg-[#D9775A] hover:bg-[#C2603F]' : ''}
                >
                  Alle
                </Button>
                <Button 
                  size="sm" 
                  variant={filter === 'bildungsgutschein' ? 'default' : 'outline'}
                  onClick={() => setFilter('bildungsgutschein')}
                  className={filter === 'bildungsgutschein' ? 'bg-[#D9775A] hover:bg-[#C2603F]' : ''}
                >
                  BG
                </Button>
                <Button 
                  size="sm" 
                  variant={filter === 'selbstzahler' ? 'default' : 'outline'}
                  onClick={() => setFilter('selbstzahler')}
                  className={filter === 'selbstzahler' ? 'bg-[#D9775A] hover:bg-[#C2603F]' : ''}
                >
                  SZ
                </Button>
                <Button 
                  size="sm" 
                  variant={filter === 'einrichtung' ? 'default' : 'outline'}
                  onClick={() => setFilter('einrichtung')}
                  className={filter === 'einrichtung' ? 'bg-[#D9775A] hover:bg-[#C2603F]' : ''}
                >
                  PE
                </Button>
              </div>

              {/* Teilnehmer-Liste */}
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {loading && !selectedTeilnehmer ? (
                  <div className="text-center py-4 text-gray-500">Laden...</div>
                ) : filteredTeilnehmer.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">Keine Teilnehmer gefunden</div>
                ) : (
                  filteredTeilnehmer.map(tn => (
                    <div
                      key={tn.id}
                      onClick={() => setSelectedTeilnehmer(tn)}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedTeilnehmer?.id === tn.id 
                          ? 'bg-[#D9775A] text-white' 
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <p className="font-medium text-sm">{tn.vorname} {tn.nachname}</p>
                      <p className={`text-xs truncate ${selectedTeilnehmer?.id === tn.id ? 'text-white/80' : 'text-gray-500'}`}>
                        {tn.kurs || 'Kein Kurs'}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Rechte Spalte: Anwesenheit */}
        <div className="col-span-8">
          {!selectedTeilnehmer ? (
            <Card>
              <CardContent className="py-16 text-center text-gray-500">
                <User size={48} className="mx-auto mb-4 opacity-30" />
                <p>Wählen Sie links einen Teilnehmer aus</p>
              </CardContent>
            </Card>
          ) : anwesenheit.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <AlertTriangle size={48} className="mx-auto mb-4 text-amber-500" />
                <h3 className="text-lg font-semibold mb-2">Keine Anwesenheitsdaten vorhanden</h3>
                <p className="text-gray-500 mb-4">
                  Für {selectedTeilnehmer.vorname} {selectedTeilnehmer.nachname} wurden noch keine Anwesenheitsdaten erstellt.
                </p>
                {selectedTeilnehmer.kurs_start ? (
                  <>
                    <p className="text-sm text-gray-400 mb-3">
                      Kursstart: {selectedTeilnehmer.kurs_start}
                      {new Date(selectedTeilnehmer.kurs_start) > new Date() && 
                        <span className="text-amber-500 ml-2">(liegt in der Zukunft)</span>
                      }
                    </p>
                    <Button
                      onClick={async () => {
                        try {
                          setSaving(true);
                          const res = await axios.post(`${API}/anwesenheit/generate/${selectedTeilnehmer.id}`);
                          if (res.data.created > 0) {
                            toast.success(`${res.data.created} Anwesenheitseinträge erstellt`);
                          } else if (res.data.existing > 0) {
                            toast.info(res.data.message);
                          } else {
                            toast.info('Kurs liegt in der Zukunft - 1 Eintrag für Starttag erstellt');
                          }
                          loadAnwesenheit();
                        } catch (err) {
                          console.error(err);
                          toast.error(err.response?.data?.detail || 'Fehler beim Generieren');
                        } finally {
                          setSaving(false);
                        }
                      }}
                      disabled={saving}
                      className="bg-[#D9775A] hover:bg-[#C2603F] text-white"
                    >
                      <Calendar size={16} className="mr-2" />
                      {saving ? 'Generiere...' : 'Anwesenheitsdaten erstellen'}
                    </Button>
                  </>
                ) : (
                  <p className="text-sm text-red-500">
                    Bitte zuerst einen Kurs und Kursstart beim Teilnehmer hinterlegen
                  </p>
                )}
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Teilnehmer-Info & Statistik */}
              <Card className="mb-4">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold">
                        {selectedTeilnehmer.vorname} {selectedTeilnehmer.nachname}
                      </h2>
                      <p className="text-sm text-gray-500">{selectedTeilnehmer.kurs}</p>
                      <p className="text-xs text-gray-400">
                        {selectedTeilnehmer.kurs_start} - {selectedTeilnehmer.kurs_ende}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-emerald-600">{stats.anwesend}</p>
                        <p className="text-xs text-gray-500">Anwesend</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-amber-600">{stats.entschuldigt}</p>
                        <p className="text-xs text-gray-500">Entsch.</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-red-600">{stats.unentschuldigt}</p>
                        <p className="text-xs text-gray-500">Unentsch.</p>
                      </div>
                      <div className="text-center border-l pl-4">
                        <p className={`text-2xl font-bold ${stats.quote >= 80 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {stats.quote}%
                        </p>
                        <p className="text-xs text-gray-500">Quote</p>
                      </div>
                      {/* Neuer Tag hinzufügen Button */}
                      <div className="border-l pl-4 flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            const datum = window.prompt("Datum für neuen Theorie-Tag (YYYY-MM-DD):", new Date().toISOString().split('T')[0]);
                            if (datum) {
                              try {
                                await axios.post(`${API}/bg-anwesenheit`, {
                                  teilnehmer_id: selectedTeilnehmer.id,
                                  datum: datum,
                                  status: "anwesend",
                                  typ: "theorie"
                                });
                                toast.success("Theorie-Tag hinzugefügt");
                                loadAnwesenheit();
                              } catch (err) {
                                toast.error("Fehler beim Hinzufügen");
                              }
                            }
                          }}
                        >
                          <Plus size={16} className="mr-1" /> Theorie
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            const datum = window.prompt("Datum für neuen Praktikums-Tag (YYYY-MM-DD):", new Date().toISOString().split('T')[0]);
                            if (datum) {
                              try {
                                await axios.post(`${API}/bg-anwesenheit`, {
                                  teilnehmer_id: selectedTeilnehmer.id,
                                  datum: datum,
                                  status: "anwesend",
                                  typ: "praktikum"
                                });
                                toast.success("Praktikums-Tag hinzugefügt");
                                loadAnwesenheit();
                              } catch (err) {
                                toast.error("Fehler beim Hinzufügen");
                              }
                            }
                          }}
                          className="bg-purple-100 hover:bg-purple-200 text-purple-800"
                        >
                          <Plus size={16} className="mr-1" /> Praktikum
                        </Button>
                      </div>
                      <div className="border-l pl-4">
                        <Button
                          onClick={async () => {
                            try {
                              setSaving(true);
                              const res = await axios.post(`${API}/anwesenheit-pdf/${selectedTeilnehmer.id}`);
                              toast.success(`PDF gespeichert: ${res.data.filename}`);
                            } catch (err) {
                              console.error(err);
                              toast.error('Fehler beim Speichern des PDFs');
                            } finally {
                              setSaving(false);
                            }
                          }}
                          disabled={saving || anwesenheit.length === 0}
                          className="bg-[#D9775A] hover:bg-[#C2603F] text-white"
                        >
                          <Save size={16} className="mr-2" />
                          {saving ? 'Speichert...' : 'PDF in Anhänge speichern'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Monats-Navigation */}
              <Card className="mb-4">
                <CardContent className="py-3">
                  <div className="flex items-center justify-between">
                    <Button variant="ghost" size="sm" onClick={prevMonth}>
                      <ChevronLeft size={18} />
                    </Button>
                    <h3 className="font-medium">
                      {currentMonth.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
                    </h3>
                    <Button variant="ghost" size="sm" onClick={nextMonth}>
                      <ChevronRight size={18} />
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Anwesenheits-Liste */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">
                      Anwesenheit ({anwesenheit.length} Tage)
                    </CardTitle>
                    <div className="flex gap-2 text-xs">
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded bg-emerald-500"></span> Anwesend
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded bg-amber-500"></span> Entsch.
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded bg-red-500"></span> Unentsch.
                      </span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="text-center py-8 text-gray-500">Laden...</div>
                  ) : anwesenheit.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      Keine Anwesenheitsdaten vorhanden
                    </div>
                  ) : (
                    <div className="space-y-1 max-h-[400px] overflow-y-auto">
                      {anwesenheit
                        .filter(a => {
                          const date = new Date(a.datum);
                          return date.getMonth() === currentMonth.getMonth() && 
                                 date.getFullYear() === currentMonth.getFullYear();
                        })
                        .sort((a, b) => new Date(a.datum) - new Date(b.datum))
                        .map((item, idx) => (
                          <div 
                            key={item.id || idx}
                            className={`flex items-center justify-between p-2 rounded-lg border ${
                              item.status === 'anwesend' ? 'bg-emerald-50 border-emerald-200' :
                              item.status === 'entschuldigt' ? 'bg-amber-50 border-amber-200' :
                              'bg-red-50 border-red-200'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-mono w-12">
                                {formatDate(item.datum)}
                              </span>
                              <span className="text-xs text-gray-500 w-8">
                                {new Date(item.datum).toLocaleDateString('de-DE', { weekday: 'short' })}
                              </span>
                              <span className="text-sm text-gray-600 truncate max-w-[200px]">
                                {item.typ === 'praktikum' ? 'Praktikum' : 'Theorie'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              {item.grund && (
                                <span className="text-xs text-gray-500 max-w-[150px] truncate" title={item.grund}>
                                  {item.grund}
                                </span>
                              )}
                              <button
                                onClick={() => cycleStatus(item)}
                                disabled={saving}
                                className={`px-3 py-1 rounded text-xs font-medium flex items-center gap-1 transition-colors ${
                                  item.status === 'anwesend' 
                                    ? 'bg-emerald-500 text-white hover:bg-emerald-600' 
                                    : item.status === 'entschuldigt'
                                    ? 'bg-amber-500 text-white hover:bg-amber-600'
                                    : 'bg-red-500 text-white hover:bg-red-600'
                                }`}
                                title="Klicken zum Ändern"
                              >
                                {item.status === 'anwesend' && <><Check size={12} /> A</>}
                                {item.status === 'entschuldigt' && <><AlertTriangle size={12} /> E</>}
                                {item.status === 'unentschuldigt' && <><X size={12} /> U</>}
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>

      {/* Dialog für Entschuldigung */}
      <Dialog open={grundDialog.open} onOpenChange={(open) => !open && setGrundDialog({ open: false, item: null, grund: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-amber-500" size={20} />
              Entschuldigung eintragen
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-4">
              {grundDialog.item && (
                <>
                  <strong>{new Date(grundDialog.item.datum).toLocaleDateString('de-DE')}</strong> - 
                  Status wird auf "Entschuldigt" gesetzt.
                </>
              )}
            </p>
            <label className="block text-sm font-medium mb-2">Grund (z.B. AU, Arzttermin)</label>
            <Input
              value={grundDialog.grund}
              onChange={(e) => setGrundDialog(prev => ({ ...prev, grund: e.target.value }))}
              placeholder="AU-Bescheinigung"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrundDialog({ open: false, item: null, grund: '' })}>
              Abbrechen
            </Button>
            <Button onClick={saveEntschuldigung} className="bg-amber-500 hover:bg-amber-600">
              <Save size={16} className="mr-2" /> Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnwesenheitNeu;
